import { FileCopyRounded } from '@mui/icons-material';
import { Widget, WidgetContent, WidgetHeader, WidgetIcon } from '@procureai/ui-mui';
import { ascend, isNotEmpty, isNotNil, map, pipe, prop, sort } from 'ramda';
import { useTranslation } from 'react-i18next';

import { PurchaseRequisition, I18nNamespace, CubeQueryData } from '~/common/types';
import { SummaryPurchaseRequisitionItemField } from '~/common/types/cubes/SummaryPurchaseRequisitionItem';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { usePurchaseRequisitionCubeData } from '~/hooks';

import { PurchaseRequisitionDetails } from './components/PurchaseRequisitionDetails/PurchaseRequisitionDetails';

export const PurchaseRequisitionDetailsWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'PurchaseRequisitionDetailsWidget' });
  const { purchaseRequisitionItems, totalValue, isLoading } = usePurchaseRequisitionCubeData(entityId);

  const hasData = isNotNil(purchaseRequisitionItems) && isNotEmpty(purchaseRequisitionItems);

  const purchaseRequisitionData: PurchaseRequisition | null = hasData
    ? {
        externalReference: purchaseRequisitionItems[0][SummaryPurchaseRequisitionItemField.EXTERNAL_ID],
        totalValue: totalValue as number,
        description: purchaseRequisitionItems[0][SummaryPurchaseRequisitionItemField.SUMMARY],
        items: pipe(
          map((item: CubeQueryData) => ({
            id: item[SummaryPurchaseRequisitionItemField.ITEM_ID],
            position: parseInt(item[SummaryPurchaseRequisitionItemField.LINE_ITEM_NUMBER]),
            title: item[SummaryPurchaseRequisitionItemField.TITLE],
            description: item[SummaryPurchaseRequisitionItemField.DESCRIPTION],
            supplier: item[SummaryPurchaseRequisitionItemField.SUPPLIER_ID],
            unit: item[SummaryPurchaseRequisitionItemField.UNIT],
            pricePerUnit: parseFloat(item[SummaryPurchaseRequisitionItemField.PRICE_PER_UNIT] ?? 0),
            quantity: parseFloat(item[SummaryPurchaseRequisitionItemField.QUANTITY]),
            currency: purchaseRequisitionItems[0][SummaryPurchaseRequisitionItemField.CURRENCY],
          })),
          sort(ascend(prop('position'))),
        )(purchaseRequisitionItems),
      }
    : null;
  let content = <PurchaseRequisitionDetails loading={isLoading} data={purchaseRequisitionData} />;

  if (!hasData && !isLoading) {
    content = <PlaceholderWidget withImage />;
  }

  return (
    <Widget hover={false} data-intercom-target="PurchaseRequisitionDetailsWidget">
      <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<FileCopyRounded fontSize="small" />} />} />
      <WidgetContent>
        <div>{content}</div>
      </WidgetContent>
    </Widget>
  );
};
